
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ScrollButtons from '../../components/feature/ScrollButtons';
import Button from '../../components/base/Button';
import Card from '../../components/base/Card';

export default function Projects() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Projects', count: 7 },
    { id: 'education', name: 'Education', count: 4 },
    { id: 'healthcare', name: 'Healthcare', count: 2 },
    { id: 'water', name: 'Clean Water', count: 1 }
  ];

  const projects = [
    {
      id: 1,
      title: "Global Grace Children Homes",
      description: "We lovingly care for over 225+ children across 9 vibrant homes, each nurturing around 25+ little ones. These children are mostly orphans or some with a single parent who don't have a source of income. At the beginning of every school year in June, children receive new garments, school bags, books, and slippers, ensuring a bright start. Come December, each child is gifted with special Christmas attire, adding warmth and cheer to their festive season.",
      image: "https://static.readdy.ai/image/d9bb5601c98f22586a2b11870925187d/61dfa05306ff630b62a6f4f03c7fd7ae.jfif",
      raised: 2500,
      goal: 54000,
      location: "Andhra Pradesh, India",
      category: "education",
      status: "active",
      urgency: "high"
    },
    {
      id: 2,
      title: "Global Grace Sewing Institute",
      description: "At our 5 well-established centers and 1 newly opened facility, we empower women with a 6-month free sewing program. Each batch trains 25 women in fundamental tailoring techniques blended with spiritual encouragement. Upon completion, every graduate receives a sewing machine and certificate, opening doors to self-reliance and hope.",
      image: "https://static.readdy.ai/image/d9bb5601c98f22586a2b11870925187d/e8a751b811a1d125786e1fc827cb3e12.jfif",
      raised: 5000,
      goal: 50000,
      location: "Andhra Pradesh, India",
      category: "education",
      status: "active",
      urgency: "medium"
    },
    {
      id: 3,
      title: "Educational Scholarships",
      description: "Since 2022, we have provided scholarships to 77 bright students, from 10th grade through postgraduate studies. This scholarships help close the gap between potential and opportunity, empowering young minds to grow into tomorrow's leaders.",
      image: "https://static.readdy.ai/image/d9bb5601c98f22586a2b11870925187d/02de37af0138bc357b6ba21c1e03d8bc.jfif",
      raised: 1130,
      goal: 14600,
      location: "Andhra Pradesh, India",
      category: "education",
      status: "active",
      urgency: "high"
    },
    {
      id: 4,
      title: "Global Grace Leprosy Home",
      description: "Across our leprosy homes, we support 110+ residents per home, providing monthly groceries and daily essentials that uphold their dignity. Once a year, our team gifts brand-new clothes, helping residents feel respected and remembered.",
      image: "https://static.readdy.ai/image/d9bb5601c98f22586a2b11870925187d/975af84e936eaf817bb83230c15d378b.jfif",
      raised: 730,
      goal: 4200,
      location: "Andhra Pradesh, India",
      category: "healthcare",
      status: "active",
      urgency: "high"
    },
    {
      id: 5,
      title: "Global Grace Widow Homes",
      description: "Our widows' home cares for 20 senior women, providing them with daily meals, groceries, and a safe, peaceful place to live. Beyond their physical needs, we offer companionship, dignity, and spiritual fellowship, ensuring that they are never alone in their golden years. Regular medical care and vaccinations are also provided to support their health and well-being.",
      image: "https://static.readdy.ai/image/d9bb5601c98f22586a2b11870925187d/2e80ed3b87cb322ca192714178614d33.jfif",
      raised: 480,
      goal: 2400,
      location: "Andhra Pradesh, India",
      category: "healthcare",
      status: "active",
      urgency: "high"
    },
    {
      id: 6,
      title: "Water Wells",
      description: "Through our clean water initiative, we've installed 500+ life-giving water wells in remote and underserved areas. With every new well, we quench thirst, promote health, and restore dignity - and many more are on the way.",
      image: "https://static.readdy.ai/image/d9bb5601c98f22586a2b11870925187d/9974a71d884e56a7fa09b400181f88f6.jfif",
      raised: 3950,
      goal: 15000,
      location: "Andhra Pradesh, India",
      category: "water",
      status: "active",
      urgency: "high"
    },
    {
      id: 7,
      title: "Pastors Support and Church Construction",
      description: "We stand beside dedicated pastors through financial and emotional support as they serve unreached communities. Simultaneously, we actively participate in constructing churches, creating sacred spaces for worship and hope to flourish.",
      image: "https://static.readdy.ai/image/d9bb5601c98f22586a2b11870925187d/9a68d70ee25aabcafafc862c12b0844c.jfif",
      raised: 0,
      goal: 30000,
      location: "Andhra Pradesh, India",
      category: "education",
      status: "active",
      urgency: "high"
    }
  ];

  const filteredProjects = selectedCategory === 'all' 
    ? projects 
    : projects.filter(project => project.category === selectedCategory);

  const getUrgencyColor = (urgency: string) => {
    switch(urgency) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Page Header */}
      <section className="bg-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Our Projects</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore our ongoing initiatives that are making real differences in communities around the world. Each project is designed to create sustainable, lasting impact.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-4 justify-center">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  selectedCategory === category.id
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category.name} ({category.count})
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => (
              <Card key={project.id} hover className="cursor-pointer">
                <div className="relative">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-48 object-cover object-top rounded-t-xl"
                  />
                  <div className="absolute top-4 right-4 bg-white px-3 py-1 rounded-full text-sm font-medium text-gray-700">
                    {project.location}
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                      {categories.find(cat => cat.id === project.category)?.name}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-3 cursor-pointer hover:text-blue-600 transition-colors">{project.title}</h3>
                  <p className="text-gray-600 mb-4 line-clamp-3">{project.description}</p>
                  
                  <div className="mb-4">
                    <div className="flex justify-between text-sm text-gray-600 mb-2">
                      <span>Raised: ${project.raised.toLocaleString()}</span>
                      <span>Goal: ${project.goal.toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min((project.raised / project.goal) * 100, 100)}%` }}
                      ></div>
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      {Math.round((project.raised / project.goal) * 100)}% completed
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="primary" 
                      size="sm"
                      onClick={() => navigate(`/donate?project=${project.id}`)}
                      className="flex-1"
                    >
                      Support This Project
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigate(`/projects/${project.id}`)}
                      className="flex-1"
                    >
                      Learn More
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <ScrollButtons />
      <Footer />
    </div>
  );
}
